import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { AlertTriangle, Mail, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"
import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { getFlowEmailDisabledReason } from "../../utils/workflow-email-capability"
import { NodeExecutionIndicator } from "../node-execution-indicator"

/**
 * Email node (Fas 4): emails the current output mid-flow. Passes the input
 * through unchanged so downstream nodes still receive it. {input} in the body
 * template is replaced with the incoming text.
 */
export function EmailNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted } = useNodeExecutionState(id)

  const handleChange = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      updateNodeData(id, { [key]: e.target.value })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const to = (data.to as string) || ""
  const subject = (data.subject as string) || ""
  const bodyTemplate = (data.bodyTemplate as string) || ""
  const disabledReason = getFlowEmailDisabledReason(SettingsService.getInstance().settings)
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      {disabledReason && (
        <div className="border-warning-ink bg-warning/30 text-warning-ink flex gap-2 rounded-md border p-2 text-[10px] leading-snug">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          <span>{disabledReason}</span>
        </div>
      )}
      <div className="grid gap-1.5">
        <Label className="text-xs">To</Label>
        <Input
          className="nodrag h-7 text-xs"
          placeholder="a@b.com, c@d.com"
          value={to}
          onChange={handleChange("to")}
        />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Subject</Label>
        <TemplateField
          className="h-7 pr-8"
          placeholder="Flow output"
          value={subject}
          onChange={(value) => updateNodeData(id, { subject: value })}
        />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Body (optional)</Label>
        <TemplateField
          multiline
          className={cn("pr-8", isModal ? "min-h-[320px]" : "min-h-16")}
          placeholder={"{input}"}
          value={bodyTemplate}
          onChange={(value) => updateNodeData(id, { bodyTemplate: value })}
        />
        <p className="text-muted-foreground text-[10px]">
          Leave empty to send the incoming text. Use {"{input}"} inside a template.
        </p>
      </div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
    </div>
  )

  return (
    <Card
      className={cn(
        "relative min-w-[230px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        data.active ? "border-success ring-success ring-2" : "border-border",
        selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[0])}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
          <Mail className="size-4" />
          Email
          <NodeEditDialog
            description="Edit this email node in a larger workspace."
            title="Email settings"
          >
            {renderEditor(true)}
          </NodeEditDialog>
        </CardTitle>
        {selected && (
          <Button
            className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
            size="icon"
            variant="destructive"
            onClick={handleDelete}
          >
            <X className="size-3" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-3 p-3">
        {renderEditor()}
        <Handle position={Position.Left} type="target" />
        <Handle position={Position.Right} type="source" />
      </CardContent>
    </Card>
  )
}
